import type { ExternalSeatData, ParsedSeatData, SeatData } from '../models/types';

export type SeatInfoIndexEntry = {
  id: number;
  price: number;
  visibility?: string | null;
  sectorName: string;
  row?: string | null;
  seat?: string | null;
  groupKey: string;
};

export type SeatInfoIndex = Map<string, SeatInfoIndexEntry>;

const makeSeatKey = (
  normalizeSectorName: (name?: string) => string,
  sector: string,
  row: string | number | null | undefined,
  seat: string | number | null | undefined
) => `${normalizeSectorName(sector)}|${String(row ?? '').trim()}|${String(seat ?? '').trim()}`;

const makeSectorKey = (normalizeSectorName: (name?: string) => string, sector: string) =>
  `sector:${normalizeSectorName(sector)}`;

const isRoyalVipFamilySector = (sectorName: string | null | undefined): boolean => {
  if (!sectorName) return false;
  const normalizedRaw = String(sectorName)
    .replace(/[–—_-]/g, ' ')
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return /^royal vip(?: \d+)?$/.test(normalizedRaw);
};

export const buildSeatInfoIndex = (
  seatsInfoData: SeatData[],
  normalizeSectorName: (name?: string) => string
): SeatInfoIndex => {
  const map = new Map<string, SeatInfoIndexEntry>();
  for (const seatInfo of seatsInfoData) {
    const rowVal = seatInfo.row ?? seatInfo.rowName ?? '';
    const seatVal = seatInfo.seat ?? seatInfo.seatName ?? '';
    const key = makeSeatKey(normalizeSectorName, seatInfo.sector, rowVal, seatVal);
    const groupKey = makeSectorKey(normalizeSectorName, seatInfo.sector);
    const entry: SeatInfoIndexEntry = {
      id: seatInfo.id,
      price: seatInfo.price,
      visibility: seatInfo.visibility,
      sectorName: seatInfo.sector,
      row: rowVal,
      seat: seatVal,
      groupKey,
    };

    map.set(key, entry);
    if (
      isRoyalVipFamilySector(seatInfo.sector) ||
      ((seatInfo.row === null || seatInfo.row === undefined) &&
        (seatInfo.seat === null || seatInfo.seat === undefined))
    ) {
      map.set(groupKey, entry);
    }
  }
  return map;
};

export const getSeatInfo = (
  seat: { sectorName: string; row: number | string; seat: number | string },
  seatInfoIndex: SeatInfoIndex,
  normalizeSectorName: (name?: string) => string
) => {
  const primaryKey = makeSeatKey(normalizeSectorName, seat.sectorName, seat.row, seat.seat);
  return (
    seatInfoIndex.get(primaryKey) ??
    seatInfoIndex.get(makeSectorKey(normalizeSectorName, seat.sectorName)) ??
    null
  );
};

export const parseSeatContainerId = (id: string) => {
  let sectorName = '';
  let row = 0;
  let seat = 0;

  try {
    const parts = id.split('.');
    const sectorIdx = parts.findIndex(
      part => part.toLowerCase() === 'sector' || part.toLowerCase() === 'сектор'
    );
    const rowIdx = parts.findIndex(part => part.toLowerCase() === 'row');
    const seatIdx = parts.findIndex(part => part.toLowerCase() === 'seat');

    if (sectorIdx !== -1 && sectorIdx + 1 < parts.length) {
      sectorName = parts[sectorIdx + 1];
    }
    if (rowIdx !== -1 && rowIdx + 1 < parts.length) {
      row = parseInt(parts[rowIdx + 1]) || 0;
    }
    if (seatIdx !== -1 && seatIdx + 1 < parts.length) {
      seat = parseInt(parts[seatIdx + 1]) || 0;
    }
  } catch {
    // Keep defaults on parse error.
  }

  return { sectorName, row, seat };
};

export const parseExternalSeats = (
  externalSeatsData: ExternalSeatData[],
  seatInfoIndex: SeatInfoIndex,
  normalizeSectorName: (name?: string) => string
): ParsedSeatData[] => {
  return externalSeatsData.map(seatData => {
    const parsed = parseSeatContainerId(seatData.id);
    const seatInfo = getSeatInfo(parsed, seatInfoIndex, normalizeSectorName);

    return {
      x: seatData.centerX,
      y: seatData.centerY,
      sectorName: parsed.sectorName,
      row: parsed.row,
      seat: parsed.seat,
      id: seatData.id,
      price: seatInfo?.price ?? 0,
      isAvailable: Boolean(seatInfo),
    };
  });
};
